import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { z } from "zod";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { StateList } from "src/utils/states";
import {
  uploadFeedback,
  submitUploadFeedback,
} from "../../../../redux/api/_hr.api";
import DeleteFeedback from "./Delete-feedback";

// ---------------- ZOD SCHEMA ----------------
export const feedbackSchema = z.object({
  location: z.string().min(1, "Location is required"),
  url: z.string().url("Valid URL is required"),

  thumbnail: z.instanceof(File, { message: "Thumbnail is required" }),

  thumbnail_key: z.string().optional(),
});

export type FeedbackValues = z.infer<typeof feedbackSchema>;

const UploadFeedback = () => {
  const form = useForm<FeedbackValues>({
    resolver: zodResolver(feedbackSchema),
  });

  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger

  const thumbnailFile = form.watch("thumbnail");
  const thumbnailPreview = thumbnailFile
    ? URL.createObjectURL(thumbnailFile)
    : "";

  const onSubmit = async (values: FeedbackValues) => {
    setLoading(true);

    try {
      // Upload thumbnail to S3
      const thumbnail_key = await uploadFeedback(values.thumbnail);

      if (!thumbnail_key) {
        toast.error("Thumbnail upload failed");
        setLoading(false);
        return;
      }

      values.thumbnail_key = thumbnail_key;

      // Submit form to backend
      await submitUploadFeedback(values);
      toast.success("Feedback uploaded successfully!");
      form.reset();
      
      // Trigger refresh in DeleteFeedback component
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }

    setLoading(false);
  };

  return (
    <>
      <HRTitleHeader
        title="Upload Feedback Video"
        info="Upload videos recorded during campaigns and sessions"
      />

      <div className="max-w-xl mx-auto p-6">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

          {/* LOCATION */}
          <div>
            <label className="font-semibold block mb-1">
              Location <span className="text-red-400">*</span>
            </label>

            <select
              {...form.register("location")}
              className="w-full rounded-xl p-3 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Select State</option>

              {StateList.map((item) => (
                <option key={item.state} value={item.state}>
                  {item.state}
                </option>
              ))}
            </select>

            {form.formState.errors.location && (
              <p className="text-red-500 text-sm">
                {form.formState.errors.location.message}
              </p>
            )}
          </div>

          {/* VIDEO URL */}
          <div>
            <label className="font-semibold block mb-1">
              Video URL <span className="text-red-400">*</span>
            </label>

            <input
              {...form.register("url")}
              placeholder="Paste video URL"
              className="w-full rounded-xl p-3 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            {form.formState.errors.url && (
              <p className="text-red-500 text-sm">
                {form.formState.errors.url.message}
              </p>
            )}
          </div>

          {/* THUMBNAIL UPLOAD */}
          <div>
            <label className="block mb-1 font-semibold">
              Thumbnail <span className="text-red-400">*</span>
            </label>

            <label className="border-2 border-dashed border-blue-400 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 transition">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-12 h-12 text-blue-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16.5v-9m0 0L8.25 11.25M12 7.5l3.75 3.75M6.75 19.5h10.5A2.25 2.25 0 0019.5 17.25V6.75A2.25 2.25 0 0017.25 4.5H6.75A2.25 2.25 0 004.5 6.75v10.5A2.25 2.25 0 006.75 19.5z"
                />
              </svg>

              <p className="mt-2 text-blue-600 font-medium">
                Click to upload thumbnail
              </p>
              <p className="text-sm text-gray-500">PNG, JPG allowed</p>

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    form.setValue("thumbnail", e.target.files[0]);
                  }
                }}
                className="hidden"
              />
            </label>

            {thumbnailPreview && (
              <div className="mt-4">
                <img
                  src={thumbnailPreview}
                  className="w-40 h-40 rounded-xl object-cover shadow-lg border"
                />
                <p className="font-semibold text-red-400 mt-1">
                  Click the box again to change thumbnail
                </p>
              </div>
            )}

            {form.formState.errors.thumbnail && (
              <p className="text-red-500 text-sm">
                {form.formState.errors.thumbnail.message}
              </p>
            )}
          </div>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white p-3 rounded-lg"
          >
            {loading ? "Uploading..." : "Upload Feedback"}
          </button>
        </form>
      </div>
      <DeleteFeedback refreshTrigger={refreshTrigger} />
    </>
  );
};

export default UploadFeedback;